import { Injectable, inject, ViewContainerRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { filter } from 'rxjs/operators';
import { StateSnapshot, StateMetadata, StateCaptureOptions, StateRestoreOptions, StateOperationResult } from '../interfaces/state-snapshot.interface';
import { StatefulComponent, isStatefulComponent } from '../interfaces/stateful-component.interface';
import { StateStorageService } from './state-storage.service';
import { findRouteConfig, shouldPreserveRoute } from '../config/route-state.config';
import { AuthStateService } from '@core/services/auth-state.service';

/**
 * Core service for capturing and restoring component state
 *
 * This service automatically manages component state preservation during navigation.
 * It works in conjunction with the NavigationInterceptor to provide seamless
 * state management without requiring components to manually handle state.
 */
@Injectable({
  providedIn: 'root'
})
export class StateSnapshotService {
  private router = inject(Router);
  private storage = inject(StateStorageService);
  private viewportScroller = inject(ViewportScroller);
  private authState = inject(AuthStateService);

  // Track active components for state capture (any component with propertyMap config)
  private activeComponents = new Map<string, any>();

  // Track pending restorations
  private pendingRestorations = new Map<string, StateSnapshot>();

  constructor() {
    this.initializeScrollPositionTracking();
    this.setupRefreshHandler();
  }

  /**
   * Register a component for state preservation
   * Components should call this in their ngOnInit
   * Accepts any component - state management works via propertyMap configuration
   */
  registerComponent(routePath: string, component: any): void {
    if (!component || typeof component !== 'object') {
      console.warn(`[StateSnapshot] Invalid component:`, component);
      return;
    }

    this.activeComponents.set(routePath, component);
    // console.log(`[StateSnapshot] Registered component for route: ${routePath}`);

    // Check if there's a pending restoration for this route
    this.checkPendingRestoration(routePath, component);
  }

  /**
   * Unregister a component (called in ngOnDestroy)
   */
  unregisterComponent(routePath: string): void {
    this.activeComponents.delete(routePath);
    // console.log(`[StateSnapshot] Unregistered component for route: ${routePath}`);
  }

  /**
   * Capture state for a route
   */
  captureState(routePath: string, options: StateCaptureOptions = {}): StateOperationResult {
    try {
      // Check if route should be preserved
      if (!options.force && !shouldPreserveRoute(routePath)) {
        return {
          success: false,
          message: `Route ${routePath} is not configured for state preservation`
        };
      }

      // Get route configuration
      const config = findRouteConfig(routePath);
      if (!config && !options.force) {
        return {
          success: false,
          message: `No configuration found for route: ${routePath}`
        };
      }

      // Get component
      const component = this.activeComponents.get(routePath);
      if (!component) {
        return {
          success: false,
          message: `No component registered for route: ${routePath}`
        };
      }

      // Capture component state (automatic or manual)
      const componentState = this.captureComponentState(component, config);

      // Filter state keys if configured (only if not using propertyMap)
      const filteredState = config?.stateKeys && !config?.propertyMap
        ? this.filterStateKeys(componentState, config.stateKeys)
        : componentState;

      // Create metadata
      const metadata: StateMetadata = {
        scrollPosition: options.includeScrollPosition !== false
          ? this.viewportScroller.getScrollPosition()[1]
          : 0,
        routeParams: this.getCurrentRouteParams(),
        queryParams: this.getCurrentQueryParams(),
        userId: this.authState.getUserId(),
        company: this.authState.currentCompany() || undefined,
        ...options.additionalMetadata
      };

      // Create snapshot
      const snapshot: StateSnapshot = {
        id: this.generateSnapshotId(routePath),
        routePath,
        routeName: config?.name || routePath,
        timestamp: Date.now(),
        expiresAt: Date.now() + (options.customExpiration || config?.cacheDuration || 300000),
        state: filteredState,
        metadata
      };

      // Store snapshot
      const strategy = config?.storageStrategy;
      this.storage.store(routePath, snapshot, strategy);

      console.log(`[StateSnapshot] Captured state for route: ${routePath}`, {
        stateKeys: Object.keys(filteredState),
        expiresIn: (snapshot.expiresAt - snapshot.timestamp) / 1000 + 's'
      });

      return {
        success: true,
        message: `State captured for route: ${routePath}`,
        snapshot
      };
    } catch (error) {
      console.error('[StateSnapshot] Error capturing state:', error);
      return {
        success: false,
        message: `Error capturing state: ${error}`,
        error: error as Error
      };
    }
  }

  /**
   * Restore state for a route
   */
  restoreState(routePath: string, options: StateRestoreOptions = {}): StateOperationResult {
    try {
      // Retrieve snapshot from storage
      const snapshot = this.storage.retrieve(routePath);
      if (!snapshot) {
        return {
          success: false,
          message: `No stored state found for route: ${routePath}`
        };
      }

      // Get component
      const component = this.activeComponents.get(routePath);
      if (!component) {
        // Component not ready yet, store for pending restoration
        this.pendingRestorations.set(routePath, snapshot);
        return {
          success: false,
          message: `Component not ready, state queued for restoration`
        };
      }

      // Restore component state (automatic or manual)
      const config = findRouteConfig(routePath);
      const restoredState = options.merge && component.getState
        ? { ...component.getState(), ...snapshot.state }
        : snapshot.state;

      this.restoreComponentState(component, restoredState, config);

      // Restore scroll position
      if (options.restoreScrollPosition !== false && snapshot.metadata.scrollPosition) {
        const delay = options.scrollRestoreDelay || 100;
        setTimeout(() => {
          this.viewportScroller.scrollToPosition([0, snapshot.metadata.scrollPosition]);
        }, delay);
      }

      console.log(`[StateSnapshot] Restored state for route: ${routePath}`, {
        stateKeys: Object.keys(snapshot.state),
        age: (Date.now() - snapshot.timestamp) / 1000 + 's'
      });

      // Apply automatic post-restoration actions based on configuration
      if (config?.postRestoration) {
        setTimeout(() => {
          this.applyPostRestorationActions(restoredState, config, component);
        }, 150);
      }

      // Call component's onStateRestored lifecycle hook if it exists
      if (typeof component.onStateRestored === 'function') {
        // Use setTimeout to allow DOM to update after setState
        setTimeout(() => {
          component.onStateRestored!(restoredState);
        }, 150); // Slightly longer delay to ensure DOM is ready
      }

      // Call onRestored callback from options
      if (options.onRestored) {
        options.onRestored();
      }

      // Clear pending restoration
      this.pendingRestorations.delete(routePath);

      return {
        success: true,
        message: `State restored for route: ${routePath}`,
        snapshot
      };
    } catch (error) {
      console.error('[StateSnapshot] Error restoring state:', error);
      return {
        success: false,
        message: `Error restoring state: ${error}`,
        error: error as Error
      };
    }
  }

  /**
   * Check if state exists for a route
   */
  hasState(routePath: string): boolean {
    return this.storage.has(routePath);
  }

  /**
   * Clear state for a specific route
   */
  clearState(routePath: string): void {
    this.storage.remove(routePath);
    this.pendingRestorations.delete(routePath);
    console.log(`[StateSnapshot] Cleared state for route: ${routePath}`);
  }

  /**
   * Clear all stored states
   */
  clearAllStates(): void {
    this.storage.clearAll();
    this.pendingRestorations.clear();
    console.log('[StateSnapshot] Cleared all states');
  }

  /**
   * Get storage statistics
   */
  getStats() {
    return {
      storage: this.storage.getStats(),
      activeComponents: this.activeComponents.size,
      pendingRestorations: this.pendingRestorations.size,
      allStoredKeys: this.storage.getAllKeys()
    };
  }

  /**
   * Debug method to inspect current state
   */
  debugState(routePath: string): void {
    const snapshot = this.storage.retrieve(routePath);
    const component = this.activeComponents.get(routePath);
    const config = findRouteConfig(routePath);

    console.group(`[StateSnapshot] Debug: ${routePath}`);
    console.log('Config:', config);
    console.log('Snapshot:', snapshot);
    console.log('Component registered:', !!component);
    console.log('Has pending restoration:', this.pendingRestorations.has(routePath));
    console.groupEnd();
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /**
   * Check if there's a pending restoration for a newly registered component
   */
  private checkPendingRestoration(routePath: string, component: any): void {
    const pendingSnapshot = this.pendingRestorations.get(routePath);
    if (pendingSnapshot) {
      // Component is now ready, restore state
      setTimeout(() => {
        this.restoreState(routePath);
      }, 0);
    } else {
      // Check if we have stored state that should be restored
      const snapshot = this.storage.retrieve(routePath);
      if (snapshot) {
        setTimeout(() => {
          this.restoreState(routePath);
        }, 0);
      }
    }
  }

  /**
   * Filter state keys based on configuration
   */
  private filterStateKeys(state: Record<string, any>, allowedKeys: string[]): Record<string, any> {
    const filtered: Record<string, any> = {};
    for (const key of allowedKeys) {
      if (key in state) {
        filtered[key] = state[key];
      }
    }
    return filtered;
  }

  /**
   * Generate unique snapshot ID
   */
  private generateSnapshotId(routePath: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(7);
    return `${routePath}-${timestamp}-${random}`;
  }

  /**
   * Get current route parameters
   */
  private getCurrentRouteParams(): Record<string, string> {
    const params: Record<string, string> = {};
    let route = this.router.routerState.root;

    while (route) {
      if (route.snapshot.params) {
        Object.assign(params, route.snapshot.params);
      }
      route = route.firstChild!;
    }

    return params;
  }

  /**
   * Get current query parameters
   */
  private getCurrentQueryParams(): Record<string, string> {
    const params: Record<string, string> = {};
    let route = this.router.routerState.root;

    while (route) {
      if (route.snapshot.queryParams) {
        Object.assign(params, route.snapshot.queryParams);
      }
      route = route.firstChild!;
    }

    return params;
  }

  /**
   * Setup handler for page refresh/reload
   * Clears state for routes with persistAcrossRefresh: false
   */
  private setupRefreshHandler(): void {
    // Listen for beforeunload event (browser refresh, close, navigate away)
    window.addEventListener('beforeunload', () => {
      // Get all stored route keys
      const storedKeys = this.storage.getAllKeys();

      storedKeys.forEach(routePath => {
        const config = findRouteConfig(routePath);

        // Clear state for routes that should not persist across refresh
        if (config && config.persistAcrossRefresh === false) {
          this.storage.remove(routePath);
          console.log(`[StateSnapshot] Cleared state for route on refresh: ${routePath}`);
        }
      });
    });
  }

  /**
   * Initialize scroll position tracking
   */
  private initializeScrollPositionTracking(): void {
    // Listen to navigation end events to potentially restore scroll
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Scroll position restoration is handled in restoreState()
        // This is just for tracking purposes
      });
  }

  /**
   * Apply automatic post-restoration actions based on route configuration
   */
  private applyPostRestorationActions(
    state: Record<string, any>,
    config: any,
    component: any
  ): void {
    const postRestoration = config.postRestoration;

    // Handle row highlighting
    if (postRestoration.highlightRow) {
      this.applyRowHighlighting(state, postRestoration.highlightRow);
    }

    // Handle custom actions
    if (postRestoration.customAction && typeof postRestoration.customAction === 'function') {
      postRestoration.customAction(state, component);
    }
  }

  /**
   * Apply row highlighting based on configuration
   */
  private applyRowHighlighting(
    state: Record<string, any>,
    config: {
      indexProperty: string;
      rowSelector?: string;
      highlightClass?: string;
      scrollIntoView?: boolean;
      restorePagination?: boolean;
      pageSize?: number;
    }
  ): void {
    const rowIndex = state[config.indexProperty];

    if (rowIndex === null || rowIndex === undefined) {
      return;
    }

    const rowSelector = config.rowSelector || '.table-row';
    const highlightClass = config.highlightClass || 'highlighted-row';
    const scrollIntoView = config.scrollIntoView !== false;

    // Calculate and restore pagination if enabled
    if (config.restorePagination && config.pageSize) {
      const pageIndex = Math.floor(rowIndex / config.pageSize);
      console.log(`[StateSnapshot] Calculated page index: ${pageIndex} for row ${rowIndex} with pageSize ${config.pageSize}`);

      // Set pagination before highlighting
      this.restorePaginationState(pageIndex, config.pageSize, rowIndex, rowSelector, highlightClass, scrollIntoView);
    } else {
      // Wait for table to restore its own pagination state (via persistState), then highlight
      // Use longer delay and smarter retry logic to wait for correct page to be rendered
      setTimeout(() => {
        console.log(`[StateSnapshot] Starting row highlighting for index ${rowIndex} after table initialization`);
        this.highlightRowWithRetry(rowIndex, rowSelector, highlightClass, scrollIntoView, 0);
      }, 1500); // Increased delay to ensure table pagination fully restored
    }
  }

  /**
   * Restore pagination state to show the correct page for the row index
   */
  private restorePaginationState(
    pageIndex: number,
    pageSize: number,
    rowIndex: number,
    rowSelector: string,
    highlightClass: string,
    scrollIntoView: boolean
  ): void {
    // Find pagination buttons and navigate to the correct page
    setTimeout(() => {
      // Look for TanStack table pagination
      const paginationContainer = document.querySelector('.pagination-controls');

      if (paginationContainer) {
        // Calculate the display page number (1-based for user)
        const displayPageNumber = pageIndex + 1;

        // Try to find and click the specific page button
        const pageButtons = paginationContainer.querySelectorAll('button');
        let pageButtonFound = false;

        pageButtons.forEach(button => {
          const buttonText = button.textContent?.trim();
          if (buttonText === displayPageNumber.toString()) {
            button.click();
            pageButtonFound = true;
            console.log(`[StateSnapshot] Navigated to page ${displayPageNumber} by clicking page button`);
          }
        });

        // If direct page button not found, navigate using next/previous buttons
        if (!pageButtonFound) {
          console.log(`[StateSnapshot] Page button ${displayPageNumber} not visible, using navigation buttons`);
          this.navigateToPageWithButtons(displayPageNumber, 0);
        }

        // After navigation, wait and highlight the row
        // Use absolute rowIndex since table now uses row.index (absolute) for data-row-index
        setTimeout(() => {
          console.log(`[StateSnapshot] Looking for absolute row index: ${rowIndex} on page ${displayPageNumber}`);
          this.highlightRowWithRetry(rowIndex, rowSelector, highlightClass, scrollIntoView, 0);
        }, 400);
      } else {
        console.warn('[StateSnapshot] Pagination container not found, highlighting without pagination');
        this.highlightRowWithRetry(rowIndex, rowSelector, highlightClass, scrollIntoView, 0);
      }
    }, 200);
  }

  /**
   * Navigate to target page using next/previous buttons
   */
  private navigateToPageWithButtons(targetPage: number, retryCount: number): void {
    const maxRetries = 3;

    // Find current page indicator
    const activePageButton = document.querySelector('.pagination-controls button.active');
    const currentPage = activePageButton ? parseInt(activePageButton.textContent?.trim() || '1', 10) : 1;

    console.log(`[StateSnapshot] Current page: ${currentPage}, Target page: ${targetPage}`);

    if (currentPage === targetPage) {
      console.log(`[StateSnapshot] Reached target page ${targetPage}`);
      return;
    }

    const paginationContainer = document.querySelector('.pagination-controls');
    if (!paginationContainer) {
      console.warn('[StateSnapshot] Pagination controls not found');
      return;
    }

    // Find next/previous buttons
    const buttons = paginationContainer.querySelectorAll('button');
    let nextButton: HTMLButtonElement | null = null;
    let prevButton: HTMLButtonElement | null = null;

    buttons.forEach(button => {
      if (button.querySelector('svg')) {
        const ariaLabel = button.getAttribute('aria-label') || '';
        if (ariaLabel.includes('Next') || button.innerHTML.includes('chevron-right')) {
          nextButton = button as HTMLButtonElement;
        } else if (ariaLabel.includes('Previous') || button.innerHTML.includes('chevron-left')) {
          prevButton = button as HTMLButtonElement;
        }
      }
    });

    if (currentPage < targetPage && nextButton && !nextButton.disabled) {
      nextButton.click();
      setTimeout(() => {
        if (retryCount < maxRetries) {
          this.navigateToPageWithButtons(targetPage, retryCount + 1);
        }
      }, 300);
    } else if (currentPage > targetPage && prevButton && !prevButton.disabled) {
      prevButton.click();
      setTimeout(() => {
        if (retryCount < maxRetries) {
          this.navigateToPageWithButtons(targetPage, retryCount + 1);
        }
      }, 300);
    } else {
      console.warn(`[StateSnapshot] Cannot navigate from page ${currentPage} to ${targetPage}`);
    }
  }

  /**
   * Highlight row with retry logic for async rendering
   * Waits for the correct page to be rendered before highlighting
   */
  private highlightRowWithRetry(
    rowIndex: number,
    rowSelector: string,
    highlightClass: string,
    scrollIntoView: boolean,
    retryCount: number
  ): void {
    const maxRetries = 15; // Increased retries for paginated tables with slow restoration

    // Remove previous highlighting
    const previousHighlighted = document.querySelectorAll(`${rowSelector}.${highlightClass}`);
    previousHighlighted.forEach(row => row.classList.remove(highlightClass));

    // Find and highlight the row
    const rowToHighlight = document.querySelector(`${rowSelector}[data-row-index="${rowIndex}"]`);
    console.log(`[StateSnapshot] Attempt ${retryCount + 1}: Looking for row index ${rowIndex}`, rowToHighlight);

    if (rowToHighlight) {
      rowToHighlight.classList.add(highlightClass);
      console.log(`[StateSnapshot] ✓ Successfully highlighted row index: ${rowIndex}`);

      // Scroll into view if configured
      if (scrollIntoView) {
        rowToHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else if (retryCount < maxRetries) {
      // Row not found yet, check current pagination state
      const allRowsWithIndex = document.querySelectorAll(`${rowSelector}[data-row-index]`);
      const availableIndices = Array.from(allRowsWithIndex).map(row => parseInt(row.getAttribute('data-row-index') || '0'));

      const minIndex = availableIndices.length > 0 ? Math.min(...availableIndices) : -1;
      const maxIndex = availableIndices.length > 0 ? Math.max(...availableIndices) : -1;

      console.log(`[StateSnapshot] Row ${rowIndex} not in DOM. Current page shows rows ${minIndex}-${maxIndex}. Retrying (${retryCount + 1}/${maxRetries})...`);

      // Use progressive backoff - longer wait if we're still on wrong page
      const isWrongPage = availableIndices.length > 0 && (rowIndex < minIndex || rowIndex > maxIndex);
      const retryDelay = isWrongPage ? 500 : 300; // Longer delay if we're on the wrong page

      setTimeout(() => {
        this.highlightRowWithRetry(rowIndex, rowSelector, highlightClass, scrollIntoView, retryCount + 1);
      }, retryDelay);
    } else {
      // Final check - log all available row indices for debugging
      const allRows = document.querySelectorAll(`${rowSelector}[data-row-index]`);
      const availableIndices = Array.from(allRows).map(row => row.getAttribute('data-row-index'));
      console.warn(`[StateSnapshot] ✗ Failed to find row ${rowIndex} after ${maxRetries} retries`);
      console.warn(`[StateSnapshot] Available row indices:`, availableIndices);
      console.warn(`[StateSnapshot] This usually means the table pagination didn't restore correctly`);
    }
  }

  /**
   * Capture component state automatically using propertyMap or manual getState
   */
  private captureComponentState(component: any, config: any): Record<string, any> {
    // If component has getState method, use it (backward compatibility)
    if (typeof component.getState === 'function') {
      return component.getState();
    }

    // Otherwise use automatic property mapping from config
    if (config?.propertyMap) {
      const state: Record<string, any> = {};

      for (const [componentProp, stateKey] of Object.entries(config.propertyMap)) {
        const value = component[componentProp];

        // Handle signals (call if function)
        if (typeof value === 'function') {
          state[stateKey as string] = value.call(component);
        } else {
          state[stateKey as string] = value;
        }
      }

      console.log('[StateSnapshot] Auto-captured state using propertyMap:', Object.keys(state));
      return state;
    }

    // Fallback: empty state
    console.warn('[StateSnapshot] No getState method or propertyMap configured');
    return {};
  }

  /**
   * Restore component state automatically using propertyMap or manual setState
   */
  private restoreComponentState(component: any, state: Record<string, any>, config: any): void {
    // If component has setState method, use it (backward compatibility)
    if (typeof component.setState === 'function') {
      component.setState(state);
      return;
    }

    // Otherwise use automatic property mapping from config
    if (config?.propertyMap) {
      for (const [componentProp, stateKey] of Object.entries(config.propertyMap)) {
        const stateValue = state[stateKey as string];

        if (stateValue !== undefined) {
          const currentValue = component[componentProp];

          // Handle signals (use .set if available)
          if (typeof currentValue === 'function' && typeof currentValue.set === 'function') {
            currentValue.set(stateValue);
          } else {
            // Direct property assignment
            component[componentProp] = stateValue;
          }
        }
      }

      console.log('[StateSnapshot] Auto-restored state using propertyMap');

      // Trigger change detection if available
      if (component.cdr && typeof component.cdr.detectChanges === 'function') {
        component.cdr.detectChanges();
      }

      return;
    }

    // Fallback: warn
    console.warn('[StateSnapshot] No setState method or propertyMap configured');
  }
}
